import type { IV_DRIP_RATE_CALCULATOR_STRINGS } from "@/components/tools/iv-drip-rate-calculator";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  dripRate,
  dripRateFromFlow,
  flowRate,
  infusionMinutes,
  infusionMinutesFromDrip,
  roundHalfUp,
  toDuration,
  totalMinutes,
} from "@/lib/tool-kit/stats-clinical/iv-drip";

/*
 * Turns the reader's inputs into result cards and worked steps for the IV drip calculator.
 * Plain functions, so the widget stays a layout and the branches stay readable.
 */

type Strings = typeof IV_DRIP_RATE_CALCULATOR_STRINGS;
type Format = (value: number, options?: Intl.NumberFormatOptions) => string;

export type IvMode = "volumeTime" | "flow" | "duration";

export interface IvInputs {
  volume: number | null;
  hours: number | null;
  minutes: number | null;
  flow: number | null;
  gtt: number | null;
  factor: number | null;
  rateGiven: "mlh" | "gtt";
  decimals: number;
}

export interface IvOutput {
  cards: [string, string][];
  steps: string[];
  micro: boolean;
}

function positive(value: number | null): value is number {
  return value !== null && value > 0;
}

export function formatDuration(
  minutes: number,
  strings: Strings,
  fmt: Format,
  locale: string
): string {
  const d = toDuration(minutes);
  return [
    d.hours > 0
      ? tPlural(strings.hoursCount, locale, d.hours, {
          count: fmt(d.hours),
        }).trim()
      : "",
    d.minutes > 0 || d.hours === 0
      ? tPlural(strings.minutesCount, locale, d.minutes, {
          count: fmt(d.minutes),
        }).trim()
      : "",
  ]
    .filter((part) => part !== "")
    .join(" ");
}

function volumeTime(
  inputs: IvInputs,
  strings: Strings,
  fmt: Format
): IvOutput | null {
  const { volume, hours, minutes, factor, decimals } = inputs;
  if (
    !positive(volume) ||
    hours === null ||
    minutes === null ||
    hours < 0 ||
    minutes < 0
  ) {
    return null;
  }
  const total = totalMinutes(hours, minutes);
  if (!(total > 0)) {
    return null;
  }
  const num = (value: number, max = 2) =>
    fmt(value, { maximumFractionDigits: max });
  const rate = flowRate(volume, total);
  const cards: [string, string][] = [
    [strings.cardFlow, num(roundHalfUp(rate, 1), 1)],
  ];
  const steps = [
    t(strings.stepFlow, {
      volume: num(volume),
      hours: num(total / 60, 3),
      rate: num(rate, 3),
    }),
  ];
  if (positive(factor)) {
    const raw = dripRate(volume, factor, total);
    const rounded = num(roundHalfUp(raw, decimals), decimals);
    cards.push([strings.cardDrip, rounded]);
    steps.push(
      t(strings.stepDrip, {
        volume: num(volume),
        df: num(factor),
        minutes: num(total, 3),
        raw: num(raw, 3),
        rounded,
      }),
      t(strings.stepDripFlow, {
        rate: num(rate, 3),
        df: num(factor),
        raw: num(dripRateFromFlow(rate, factor), 3),
        rounded,
      })
    );
  }
  return { cards, steps, micro: factor === 60 };
}

function fromFlow(
  inputs: IvInputs,
  strings: Strings,
  fmt: Format
): IvOutput | null {
  const { flow, factor, decimals } = inputs;
  if (!(positive(flow) && positive(factor))) {
    return null;
  }
  const num = (value: number, max = 2) =>
    fmt(value, { maximumFractionDigits: max });
  const raw = dripRateFromFlow(flow, factor);
  const rounded = num(roundHalfUp(raw, decimals), decimals);
  return {
    cards: [[strings.cardDrip, rounded]],
    steps: [
      t(strings.stepDripFlow, {
        rate: num(flow),
        df: num(factor),
        raw: num(raw, 3),
        rounded,
      }),
    ],
    micro: factor === 60,
  };
}

function duration(
  inputs: IvInputs,
  strings: Strings,
  fmt: Format,
  locale: string
): IvOutput | null {
  const { volume, flow, gtt, factor, rateGiven } = inputs;
  if (!positive(volume)) {
    return null;
  }
  const num = (value: number, max = 2) =>
    fmt(value, { maximumFractionDigits: max });
  if (rateGiven === "mlh") {
    if (!positive(flow)) {
      return null;
    }
    const minutes = infusionMinutes(volume, flow);
    const text = formatDuration(minutes, strings, fmt, locale);
    return {
      cards: [[strings.cardTime, text]],
      steps: [
        t(strings.stepTime, {
          volume: num(volume),
          rate: num(flow),
          hours: num(minutes / 60, 3),
          duration: text,
        }),
      ],
      micro: false,
    };
  }
  if (!(positive(gtt) && positive(factor))) {
    return null;
  }
  const minutes = infusionMinutesFromDrip(volume, factor, gtt);
  const text = formatDuration(minutes, strings, fmt, locale);
  return {
    cards: [[strings.cardTime, text]],
    steps: [
      t(strings.stepTimeDrip, {
        volume: num(volume),
        df: num(factor),
        rate: num(gtt),
        minutes: num(minutes, 2),
        duration: text,
      }),
    ],
    micro: false,
  };
}

export function computeIv(
  mode: IvMode,
  inputs: IvInputs,
  strings: Strings,
  fmt: Format,
  locale: string
): IvOutput | null {
  if (mode === "volumeTime") {
    return volumeTime(inputs, strings, fmt);
  }
  return mode === "flow"
    ? fromFlow(inputs, strings, fmt)
    : duration(inputs, strings, fmt, locale);
}
